export const GYM_STATUSES = ['DRAFT', 'ACTIVE', 'SUSPENDED'] as const;
export type GymStatus = (typeof GYM_STATUSES)[number];

export const MEMBER_STATUSES = ['ACTIVE', 'INACTIVE', 'BLOCKED'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

export const CREDENTIAL_TYPES = ['RFID_CARD', 'QR_CODE', 'WRISTBAND', 'PIN', 'MOBILE'] as const;
export type CredentialType = (typeof CREDENTIAL_TYPES)[number];

export const CREDENTIAL_STATUSES = ['ACTIVE', 'DISABLED', 'LOST'] as const;
export type CredentialStatus = (typeof CREDENTIAL_STATUSES)[number];

export const MEMBERSHIP_STATUSES = [
  'PENDING',
  'ACTIVE',
  'PAUSED',
  'EXPIRED',
  'CANCELLED',
  'SUSPENDED',
] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

export const MEMBERSHIP_TRANSITIONS = [
  'ACTIVATE',
  'RENEW',
  'PAUSE',
  'RESUME',
  'EXPIRE',
  'CANCEL',
  'SUSPEND',
  'UNSUSPEND',
  'DELETE',
  'RESTORE',
] as const;
export type MembershipTransition = (typeof MEMBERSHIP_TRANSITIONS)[number];

export const PAUSE_STATUSES = [
  'REQUESTED',
  'APPROVED',
  'ACTIVE',
  'ENDED',
  'REJECTED',
  'CANCELLED',
] as const;
export type PauseStatus = (typeof PAUSE_STATUSES)[number];

export const BILLING_INTERVALS = ['ONE_TIME', 'MONTHLY', 'YEARLY'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export const ORDER_STATUSES = ['PENDING', 'PAID', 'FAILED', 'CANCELLED', 'REFUNDED'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PAYMENT_STATUSES = [
  'PENDING',
  'AUTHORIZED',
  'PAID',
  'FAILED',
  'CANCELLED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_TRANSACTION_TYPES = ['CHECKOUT', 'CAPTURE', 'REFUND', 'STATUS_SYNC'] as const;
export type PaymentTransactionType = (typeof PAYMENT_TRANSACTION_TYPES)[number];

export const REFUND_STATUSES = ['REQUESTED', 'COMPLETED', 'FAILED', 'PENDING_APPROVAL', 'REJECTED'] as const;
export type RefundStatus = (typeof REFUND_STATUSES)[number];

export const BOOKING_STATUSES = ['BOOKED', 'CANCELLED', 'ATTENDED', 'NO_SHOW', 'WAITLISTED'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const SESSION_STATUSES = ['SCHEDULED', 'CANCELLED', 'COMPLETED'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const APPOINTMENT_STATUSES = ['BOOKED', 'COMPLETED', 'NO_SHOW', 'CANCELLED'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

/** Who hosts an appointment type: a trainer from the roster, or staff holding one role (a tour given by reception). */
export const APPOINTMENT_HOST_KINDS = ['TRAINER', 'STAFF'] as const;
export type AppointmentHostKind = (typeof APPOINTMENT_HOST_KINDS)[number];

export const APPOINTMENT_CANCELLERS = ['MEMBER', 'HOST', 'STAFF'] as const;
export type AppointmentCanceller = (typeof APPOINTMENT_CANCELLERS)[number];

/** The host's answer to a member's request; null on an appointment whose type needs no approval. */
export const APPOINTMENT_APPROVALS = ['PENDING', 'APPROVED', 'DECLINED', 'EXPIRED'] as const;
export type AppointmentApproval = (typeof APPOINTMENT_APPROVALS)[number];

/** A change to a host's week on one date: time off (a whole day, or some hours) or hours added. */
export const HOST_EXCEPTION_KINDS = ['BLOCK', 'EXTRA'] as const;
export type HostExceptionKind = (typeof HOST_EXCEPTION_KINDS)[number];

/** A plan sells either time at the gym (a membership) or a number of one-to-one sessions of one appointment type. */
export const PLAN_KINDS = ['MEMBERSHIP', 'SESSION_PACKAGE'] as const;
export type PlanKind = (typeof PLAN_KINDS)[number];

export const SESSION_PACKAGE_MOVEMENT_KINDS = ['GRANTED', 'BOOKED', 'RETURNED', 'REVOKED'] as const;
export type SessionPackageMovementKind = (typeof SESSION_PACKAGE_MOVEMENT_KINDS)[number];

export const ACCESS_RESULTS = ['GRANTED', 'DENIED'] as const;
export type AccessResult = (typeof ACCESS_RESULTS)[number];

export const ACCESS_DENIAL_REASONS = [
  'MEMBER_NOT_FOUND',
  'MEMBER_BLOCKED',
  'MEMBERSHIP_NOT_FOUND',
  'MEMBERSHIP_PENDING',
  'MEMBERSHIP_EXPIRED',
  'MEMBERSHIP_PAUSED',
  'MEMBERSHIP_CANCELLED',
  'MEMBERSHIP_SUSPENDED',
  'NO_ACCESS_TODAY',
  'OUTSIDE_ALLOWED_HOURS',
  'VISIT_LIMIT_REACHED',
  'INVALID_CREDENTIAL',
  'INVALID_READER',
  'ZONE_NOT_ALLOWED',
  'LOCATION_NOT_ALLOWED',
  'ANTI_PASSBACK',
  'PROVIDER_ERROR',
  'UNKNOWN',
] as const;
export type AccessDenialReason = (typeof ACCESS_DENIAL_REASONS)[number];

export const ACCESS_DIRECTIONS = ['IN', 'OUT', 'UNKNOWN'] as const;
export type AccessDirection = (typeof ACCESS_DIRECTIONS)[number];

export const READER_KINDS = ['TURNSTILE', 'DOOR', 'DESK', 'LOCKER', 'OTHER'] as const;
export type ReaderKind = (typeof READER_KINDS)[number];

export const DOMAIN_TYPES = ['SUBDOMAIN', 'CUSTOM_DOMAIN'] as const;
export type DomainType = (typeof DOMAIN_TYPES)[number];

export const DOMAIN_VERIFICATION_STATUSES = ['PENDING', 'VERIFIED', 'FAILED'] as const;
export type DomainVerificationStatus = (typeof DOMAIN_VERIFICATION_STATUSES)[number];

export const WEBHOOK_STATUSES = ['RECEIVED', 'PROCESSING', 'PROCESSED', 'FAILED', 'IGNORED'] as const;
export type WebhookStatus = (typeof WEBHOOK_STATUSES)[number];

export const NOTIFICATION_CHANNELS = ['EMAIL', 'IN_APP', 'PUSH', 'SMS'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_STATUSES = ['QUEUED', 'SENT', 'FAILED', 'READ'] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

export const SMS_DELIVERY_STATUSES = ['DELIVERED', 'UNDELIVERED', 'EXPIRED', 'PENDING', 'UNKNOWN'] as const;
export type SmsDeliveryStatus = (typeof SMS_DELIVERY_STATUSES)[number];

export const NOTIFICATION_TEMPLATES = [
  'WELCOME',
  'EMAIL_VERIFICATION',
  'PASSWORD_RESET',
  'MEMBERSHIP_PURCHASED',
  'MEMBERSHIP_RENEWED',
  'MEMBERSHIP_EXPIRING',
  'MEMBERSHIP_EXPIRED',
  'PAYMENT_RECEIPT',
  'BOOKING_CONFIRMED',
  'BOOKING_WAITLISTED',
  'BOOKING_CANCELLED',
  'WAITLIST_PROMOTED',
  'CLASS_REMINDER',
  'CLASS_CANCELLED',
  'CLASS_CHANGED',
  'MEMBERSHIP_PAUSED',
  'MEMBERSHIP_RESUMED',
  'MEMBER_INVITATION',
  'MEMBER_LINK_CONFIRMATION',
  'ADMIN_NOTICE',
  'LEAVE_REQUESTED',
  'LEAVE_DECIDED',
  'PAYSLIP_READY',
  'FINANCE_DEADLINE',
  'SHIFT_SWAP_OFFERED',
  'SHIFT_SWAP_AWAITING_APPROVAL',
  'SHIFT_SWAP_DECIDED',
  'CAMPAIGN_OFFER',
  'LEAD_FOLLOW_UP',
  'CORPORATE_QUOTE',
  'CORPORATE_INVOICE',
  'MAINTENANCE_TICKET_OPENED',
  'APPOINTMENT_BOOKED',
  'APPOINTMENT_BOOKED_BY_HOST',
  'APPOINTMENT_CANCELLED_HOST_AWAY',
  'APPOINTMENT_CANCELLED',
  'APPOINTMENT_REMINDER',
  'APPOINTMENT_REQUESTED',
  'APPOINTMENT_APPROVED',
  'APPOINTMENT_DECLINED',
  'APPOINTMENT_REQUEST_EXPIRED',
  'CHECKLIST_DUE',
  'RECOGNITION_REMINDER',
  'KUDO_RECEIVED',
  'AWARD_RECEIVED',
  'KB_READING_REMINDER',
  'WEBHOOK_DISABLED',
  'BIRTHDAY_MEMBER',
  'BIRTHDAY_STAFF',
  'EMPLOYEE_DOCUMENT_EXPIRING',
  'EMPLOYEE_PROBATION_ENDING',
  'EMPLOYEE_CONTRACT_ENDING',
  'REVIEW_SHARED',
  'WARNING_ISSUED',
  'REVIEW_DUE',
  'SHIFT_EXTENDED',
  'INTERVIEW_SCHEDULED',
  'INTERVIEW_MOVED',
  'INTERVIEW_CANCELLED',
] as const;
export type NotificationTemplate = (typeof NOTIFICATION_TEMPLATES)[number];

export const JOB_STATUSES = ['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'DEAD'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const LOCALES = ['en', 'ka'] as const;
export type Locale = (typeof LOCALES)[number];

export const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const PAYMENT_METHODS = ['ONLINE', 'CASH', 'CARD_TERMINAL', 'BANK_TRANSFER'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_PURPOSES = ['MEMBERSHIP', 'WALLET_TOP_UP', 'APPOINTMENT'] as const;
export type PaymentPurpose = (typeof PAYMENT_PURPOSES)[number];
export const REVENUE_PAYMENT_PURPOSES = ['MEMBERSHIP', 'APPOINTMENT'] as const satisfies readonly PaymentPurpose[];

export const MANUAL_PAYMENT_METHODS = ['CASH', 'CARD_TERMINAL', 'BANK_TRANSFER'] as const;
export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];

export const WALLET_TRANSACTION_KINDS = ['TOP_UP', 'BAR_CHARGE', 'BAR_REFUND', 'CORRECTION', 'TOP_UP_REFUND'] as const;
export type WalletTransactionKind = (typeof WALLET_TRANSACTION_KINDS)[number];

export const WALLET_TOP_UP_METHODS = ['CASH', 'CARD_TERMINAL'] as const satisfies readonly ManualPaymentMethod[];
export type WalletTopUpMethod = (typeof WALLET_TOP_UP_METHODS)[number];

export const CASH_DRAWER_KINDS = ['DESK', 'BAR'] as const;
export type CashDrawerKind = (typeof CASH_DRAWER_KINDS)[number];

export const BAR_PAYMENT_METHODS = ['WALLET', 'CASH', 'CARD_TERMINAL'] as const;
export type BarPaymentMethod = (typeof BAR_PAYMENT_METHODS)[number];

export const BAR_SALE_STATUSES = ['COMPLETED', 'PARTIALLY_REFUNDED', 'REFUNDED'] as const;
export type BarSaleStatus = (typeof BAR_SALE_STATUSES)[number];

export const STOCK_MOVEMENT_KINDS = ['SALE', 'REFUND', 'DELIVERY', 'COUNT', 'WRITE_OFF'] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

export const STOCK_WRITE_OFF_REASONS = ['EXPIRED', 'DAMAGED', 'STAFF_USE', 'GIVEAWAY', 'OTHER'] as const;
export type StockWriteOffReason = (typeof STOCK_WRITE_OFF_REASONS)[number];

export const CONTENT_PAGE_KINDS = ['LEGAL', 'PAGE', 'NOTICE'] as const;
export type ContentPageKind = (typeof CONTENT_PAGE_KINDS)[number];

export const ORDER_KINDS = ['PURCHASE', 'RENEWAL'] as const;
export type OrderKind = (typeof ORDER_KINDS)[number];

export const CAMPAIGN_KINDS = ['SALE', 'NEW_MEMBER', 'ANNOUNCEMENT', 'PROMO_CODE', 'BONUS_DAYS'] as const;

/** Why a promo code gives nothing: no such code, not running now, every use taken, this member's uses taken, or no discount on the plan. */
export const PROMO_CODE_REASONS = ['UNKNOWN', 'NOT_LIVE', 'USED_UP', 'ALREADY_USED', 'NOT_FOR_PLAN'] as const;
export type PromoCodeReason = (typeof PROMO_CODE_REASONS)[number];
export type CampaignKind = (typeof CAMPAIGN_KINDS)[number];

export const CAMPAIGN_SMS_AUDIENCES = ['ALL', 'ACTIVE', 'LAPSED', 'SEGMENT'] as const;
export type CampaignSmsAudience = (typeof CAMPAIGN_SMS_AUDIENCES)[number];

export const CAMPAIGN_EMAIL_AUDIENCES = CAMPAIGN_SMS_AUDIENCES;
export type CampaignEmailAudience = CampaignSmsAudience;

/** A member tag's colour: one of the chart palette's eight validated hues (`--color-series-*`), or grey. */
export const MEMBER_TAG_COLORS = ['gray', 'blue', 'orange', 'teal', 'amber', 'pink', 'green', 'purple', 'red'] as const;
export type MemberTagColor = (typeof MEMBER_TAG_COLORS)[number];

export const CONSENT_PURPOSES = ['SMS_OFFERS', 'EMAIL_OFFERS'] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

/** Who changed a consent: the member themself, staff on the member's word, the gateway's STOP list, or the stop link in an offer by email. */
export const CONSENT_SOURCES = ['MEMBER', 'STAFF', 'GATEWAY_STOP', 'EMAIL_LINK'] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number];

export const SAVED_CARD_STATUSES = ['ACTIVE', 'REMOVED'] as const;
export type SavedCardStatus = (typeof SAVED_CARD_STATUSES)[number];

export const RENEWAL_STATUSES = ['PENDING', 'PAID', 'FAILED', 'SKIPPED'] as const;
export type RenewalStatus = (typeof RENEWAL_STATUSES)[number];

export const ACCEPTANCE_CHANNELS = ['ONLINE', 'DESK'] as const;
export type AcceptanceChannel = (typeof ACCEPTANCE_CHANNELS)[number];

export const REPORT_VISIBILITIES = ['PRIVATE', 'SHARED'] as const;
export type ReportVisibility = (typeof REPORT_VISIBILITIES)[number];

export const DASHBOARD_PAGES = ['OVERVIEW', 'FINANCE', 'PEOPLE'] as const;
export type DashboardPage = (typeof DASHBOARD_PAGES)[number];

export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export const EMPLOYMENT_TYPES = ['FULL_TIME', 'PART_TIME', 'CONTRACTOR'] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const PAY_SCHEMES = ['MONTHLY', 'HOURLY', 'PER_SESSION'] as const;
export type PayScheme = (typeof PAY_SCHEMES)[number];

export const LEAVE_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'WITHDRAWN', 'CANCELLATION_REQUESTED', 'CANCELLED'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];
export const FINANCE_TASK_STATUSES = ['OPEN', 'DONE', 'SKIPPED'] as const;
export type FinanceTaskStatus = (typeof FINANCE_TASK_STATUSES)[number];

export const HARVEST_PROMOTION_OUTCOMES = ['PROMOTED', 'ALREADY_PROMOTED', 'NO_NAME', 'NO_CARD', 'CARD_IN_USE'] as const;
export type HarvestPromotionOutcome = (typeof HARVEST_PROMOTION_OUTCOMES)[number];

export const HARVEST_IMPORTED_MEMBERSHIPS = ['ACTIVE', 'EXPIRED', 'NONE'] as const;
export type HarvestImportedMembership = (typeof HARVEST_IMPORTED_MEMBERSHIPS)[number];

export const LATE_ONLINE_PAYMENT_MODES = ['FLAG', 'NEXT_OPEN_MONTH'] as const;
export type LateOnlinePaymentMode = (typeof LATE_ONLINE_PAYMENT_MODES)[number];

export const SESSION_ONLINE_PAYMENT_MODES = ['OFF', 'OPTIONAL', 'REQUIRED'] as const;
export type SessionOnlinePaymentMode = (typeof SESSION_ONLINE_PAYMENT_MODES)[number];
export const LEAVE_ATTACHMENT_STAGES = ['BEFORE_APPROVAL', 'AFTER_APPROVAL'] as const;
export type LeaveAttachmentStage = (typeof LEAVE_ATTACHMENT_STAGES)[number];
/** Statuses that hold days: they count against the allowance and show on the calendar. */
export const LEAVE_HOLDING_STATUSES = ['PENDING', 'APPROVED', 'CANCELLATION_REQUESTED'] as const satisfies readonly LeaveStatus[];
/** The built-in sick leave type's key, which never changes; "Report sick" files this type. */
export const SICK_LEAVE_TYPE_KEY = 'sick';

/** WEEKLY: a shift or a day off for each weekday. ROTATION: a cycle of days (3 on / 3 off …) counted from a day-1 date. */
export const SCHEDULE_KINDS = ['WEEKLY', 'ROTATION'] as const;
export type ScheduleKind = (typeof SCHEDULE_KINDS)[number];
export const SHIFT_OVERRIDE_KINDS = ['ADD', 'REMOVE'] as const;
export type ShiftOverrideKind = (typeof SHIFT_OVERRIDE_KINDS)[number];
export const SHIFT_SWAP_STATUSES = ['OFFERED', 'AWAITING_APPROVAL', 'APPROVED', 'DECLINED', 'REJECTED', 'WITHDRAWN'] as const;
export type ShiftSwapStatus = (typeof SHIFT_SWAP_STATUSES)[number];
export const SHIFT_SWAP_OPEN_STATUSES = ['OFFERED', 'AWAITING_APPROVAL'] as const satisfies readonly ShiftSwapStatus[];
export const SHIFT_SWAP_ACTIONS = ['accept', 'decline', 'withdraw', 'approve', 'reject'] as const;
export type ShiftSwapAction = (typeof SHIFT_SWAP_ACTIONS)[number];
/** OFF: no swaps. APPROVAL: the colleague accepts, then a manager approves. FREE: the colleague's yes is enough. */
export const SHIFT_SWAP_MODES = ['OFF', 'APPROVAL', 'FREE'] as const;
export type ShiftSwapMode = (typeof SHIFT_SWAP_MODES)[number];

export const PAYROLL_RUN_STATUSES = ['DRAFT', 'APPROVED', 'PAID'] as const;
export type PayrollRunStatus = (typeof PAYROLL_RUN_STATUSES)[number];

export const LEAVE_ACTIONS = ['approve', 'reject', 'withdraw', 'request_cancellation', 'approve_cancellation', 'keep'] as const;
export type LeaveAction = (typeof LEAVE_ACTIONS)[number];
